import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAdminUsers } from '../manageAdmin/context/AdminUsersContext';
import { AdminPermission } from '../manageAdmin/AdminUserList.types';
import { getUser } from '../../services/utils';
import { usePaymentsTestingAccess } from './PaymentsTestingGate';
import {
  paymentOperatorAlertsApi,
  PaymentOperatorAlert,
  PaymentOperatorAlertPage,
} from './payment-operator-alerts.api';

const TITLES: Record<PaymentOperatorAlert['kind'], string> = {
  failed: 'Source refresh failed or incomplete',
  stale: 'Source evidence is stale',
  unexpectedly_empty: 'Unexpectedly empty source',
  overdue: 'Source refresh is overdue',
  unreconciled: 'Reconciliation needs attention',
};

const RECOVERY: Record<PaymentOperatorAlert['kind'], string> = {
  failed:
    'Open the week to check the failed source and retry status. Correct the source or access issue before fetching source data again.',
  stale:
    'Open the week and use Fetch latest source data when no refresh is running. Verify all required sources and reconciliation complete.',
  unexpectedly_empty:
    'Have engineering verify source visibility and coverage before retrying. An empty response is not proof that there are no payments.',
  overdue:
    'Have engineering inspect the running refresh and lock. Do not force-unlock it or start overlapping work.',
  unreconciled:
    'Open the week and follow its recovery guidance. Missing or stale matching, or a failed source refresh, requires Fetch latest source data. Use Refresh reconciliation only when both matching and sources are current.',
};

function time(value: string): string {
  return new Date(value).toLocaleString();
}

export function PaymentOperatorAlertsPanel({
  data,
  loading,
  error,
  includeResolved,
  offset,
  canAcknowledge,
  pendingId,
  mutationFailed,
  onHistoryChange,
  onPageChange,
  onAcknowledge,
  onRetry,
}: {
  data?: PaymentOperatorAlertPage;
  loading: boolean;
  error: boolean;
  includeResolved: boolean;
  offset: number;
  canAcknowledge: boolean;
  pendingId?: string;
  mutationFailed: boolean;
  onHistoryChange: (value: boolean) => void;
  onPageChange: (offset: number) => void;
  onAcknowledge: (id: string) => void;
  onRetry: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <section
      aria-label="Payments operator alerts"
      className="mb-4 min-w-0 rounded-lg border border-slate-300 bg-white p-4 text-slate-900 shadow-sm"
    >
      <div className="flex min-h-11 flex-wrap items-center justify-between gap-3">
        <div aria-live="polite" className="min-w-0">
          <h2 className="font-semibold leading-5">Payments alerts</h2>
          <p className="mt-1 text-sm leading-5 text-slate-700">
            {error
              ? 'Alert status unavailable — do not assume all clear.'
              : loading
                ? 'Checking recorded alerts…'
                : `${data?.unresolvedCount ?? 0} unresolved · ${data?.unacknowledgedCount ?? 0} not acknowledged`}
          </p>
        </div>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls="payments-operator-alert-list"
          onClick={() => setExpanded(!expanded)}
          className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold leading-5 hover:bg-slate-100"
        >
          {expanded ? 'Hide details' : 'View alerts'}
        </button>
      </div>
      {error && (
        <button type="button" onClick={onRetry} className="mt-2 text-sm underline">
          Retry alert check
        </button>
      )}
      {expanded && (
        <div id="payments-operator-alert-list" className="mt-4 border-t border-slate-200 pt-4">
          <p className="text-sm text-slate-700">
            Acknowledgment records who saw the incident. It does not fix the source, clear readiness
            blockers, or approve payments. This feed does not replace the week’s readiness checks.
          </p>
          <label className="my-4 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeResolved}
              onChange={(event) => onHistoryChange(event.target.checked)}
            />
            Include recovered / closed history
          </label>
          {mutationFailed && (
            <p role="alert" className="mb-3 text-sm text-red-800">
              Acknowledgment could not be saved. Reload the alerts and try again.
            </p>
          )}
          {!loading && !error && data?.items.length === 0 && (
            <p className="text-sm">
              {data.total === 0
                ? `No recorded ${includeResolved ? '' : 'unresolved '}Payments alerts.`
                : 'This page is now empty. Use Previous to view the remaining incidents.'}
            </p>
          )}
          <ul className="space-y-3">
            {(data?.items ?? []).map((alert) => (
              <li key={alert.id} className="rounded-lg border border-slate-200 p-4">
                <div className="flex flex-col items-start justify-between gap-3 sm:flex-row">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold">
                      {TITLES[alert.kind]} · {alert.source === 'all' ? 'Payments' : alert.source}
                    </h3>
                    <Link
                      reloadDocument
                      to={`/payments/weekly?week=${alert.paymentWeekStart}`}
                      className="text-sm font-semibold text-teal-800 underline"
                    >
                      Open week of {alert.paymentWeekStart}
                    </Link>
                    <p className="mt-1 text-sm">
                      {alert.resolvedAt
                        ? alert.recovery === 'week_locked'
                          ? 'Closed — week locked'
                          : 'Recovered'
                        : 'Unresolved'}
                      {alert.acknowledgedAt
                        ? ` · Acknowledged ${time(alert.acknowledgedAt)}`
                        : ' · Not acknowledged'}
                    </p>
                  </div>
                  {!alert.resolvedAt && !alert.acknowledgedAt && canAcknowledge && (
                    <button
                      type="button"
                      disabled={!!pendingId || error}
                      onClick={() => onAcknowledge(alert.id)}
                      className="rounded border border-teal-700 px-3 py-2 text-sm font-semibold text-teal-800 disabled:opacity-50"
                    >
                      {pendingId === alert.id ? 'Saving…' : 'Acknowledge'}
                    </button>
                  )}
                </div>
                {!alert.resolvedAt && (
                  <p className="mt-3 text-sm leading-6">{RECOVERY[alert.kind]}</p>
                )}
                <details className="mt-3 text-xs text-slate-600">
                  <summary className="cursor-pointer">Incident and audit details</summary>
                  <dl className="mt-2 grid gap-1 break-all">
                    <dt>Incident</dt>
                    <dd>{alert.id}</dd>
                    <dt>Reason</dt>
                    <dd>{alert.reasonCode}</dd>
                    <dt>Latest run</dt>
                    <dd>{alert.latestRunId}</dd>
                    <dt>First observed</dt>
                    <dd>{time(alert.firstObservedAt)}</dd>
                    <dt>Last observed</dt>
                    <dd>{time(alert.lastObservedAt)}</dd>
                    {alert.acknowledgedByUserId && (
                      <>
                        <dt>Acknowledged by (user ID)</dt>
                        <dd>{alert.acknowledgedByUserId}</dd>
                      </>
                    )}
                    {alert.resolvedAt && (
                      <>
                        <dt>Closed / recovered</dt>
                        <dd>{time(alert.resolvedAt)}</dd>
                      </>
                    )}
                  </dl>
                </details>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
            <button
              type="button"
              disabled={offset === 0 || loading}
              onClick={() => onPageChange(Math.max(0, offset - 25))}
              className="underline disabled:opacity-40"
            >
              Previous
            </button>
            <span>{data?.total ?? 0} recorded incidents</span>
            <button
              type="button"
              disabled={loading || offset + 25 >= (data?.total ?? 0)}
              onClick={() => onPageChange(offset + 25)}
              className="underline disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export function PaymentOperatorAlerts() {
  const { myPermissions } = useAdminUsers();
  const { hasAccess, isLoading } = usePaymentsTestingAccess();
  const user = getUser();
  const userId = typeof user?.id === 'string' ? user.id : '';
  const canRead = hasAccess && !isLoading && !!userId;
  const canAcknowledge = canRead && myPermissions.includes(AdminPermission.RECONCILE_PAYMENT_CLOSE);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [offset, setOffset] = useState(0);
  const queryClient = useQueryClient();
  const scope = ['payment-operator-alerts', userId];
  const query = useQuery({
    queryKey: [...scope, includeResolved, offset],
    queryFn: ({ signal }) => paymentOperatorAlertsApi.list(includeResolved, offset, signal),
    enabled: canRead,
    retry: false,
    refetchInterval: (state) =>
      isAxiosError(state.state.error) &&
      [401, 403].includes(state.state.error.response?.status ?? 0)
        ? false
        : 30_000,
    refetchOnWindowFocus: true,
  });
  const mutation = useMutation({
    mutationFn: (id: string) => {
      if (!canAcknowledge)
        return Promise.reject(new Error('Payment acknowledgment is not permitted.'));
      return paymentOperatorAlertsApi.acknowledge(id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: scope }),
  });
  if (
    !canRead ||
    (isAxiosError(query.error) && [401, 403].includes(query.error.response?.status ?? 0))
  )
    return null;
  return (
    <PaymentOperatorAlertsPanel
      data={query.data}
      loading={query.isPending}
      error={query.isError || query.isPaused}
      includeResolved={includeResolved}
      offset={offset}
      canAcknowledge={canAcknowledge}
      pendingId={mutation.isPending ? mutation.variables : undefined}
      mutationFailed={mutation.isError}
      onHistoryChange={(value) => {
        setIncludeResolved(value);
        setOffset(0);
      }}
      onPageChange={setOffset}
      onAcknowledge={(id) => mutation.mutate(id)}
      onRetry={() => {
        query.refetch().catch(() => undefined);
      }}
    />
  );
}
